import { useCallback, useMemo, useRef, useState } from "react";

/**
 * useUploadQueue — shared state manager for pick / upload / progress / cancel.
 *
 *   const q = useUploadQueue({
 *     autoStart: true,
 *     uploader: async (file, { onProgress, signal }) => { ... },
 *   });
 *
 *   q.enqueue(file)          // add File → entry with status="pending" (or "uploading" if autoStart)
 *   q.entries                // reactive [{ id, file, status, progress, uploadedBytes, error }]
 *   q.cancel(id)             // abort an active upload
 *   q.remove(id)             // drop an entry from the list
 *   q.uploadAll(customUploader?)  // start all pending entries (for deferred contexts)
 *   q.reset()                // clear everything
 *
 * status values: "pending" | "uploading" | "done" | "error" | "canceled"
 */
export default function useUploadQueue({ autoStart = false, uploader } = {}) {
  const [entries, setEntries] = useState([]);
  const controllers = useRef(new Map());       // id → AbortController
  const idCounter = useRef(0);
  const uploaderRef = useRef(uploader);
  uploaderRef.current = uploader;

  const nextId = () => {
    idCounter.current += 1;
    return `u${Date.now()}-${idCounter.current}`;
  };

  const patch = (id, mut) =>
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...mut } : e)));

  const startUpload = useCallback(async (id, file, effectiveUploader, meta) => {
    const fn = effectiveUploader || uploaderRef.current;
    if (!fn) return;
    patch(id, { status: "uploading", progress: 0, uploadedBytes: 0, error: null });
    const ctrl = new AbortController();
    controllers.current.set(id, ctrl);
    try {
      await fn(file, {
        onProgress: (pct, loaded) => patch(id, { progress: pct, uploadedBytes: loaded }),
        signal: ctrl.signal,
        meta,
      });
      patch(id, { status: "done", progress: 100, uploadedBytes: file.size });
    } catch (err) {
      const aborted = err?.name === "CanceledError" || err?.name === "AbortError" || ctrl.signal.aborted;
      if (aborted) {
        patch(id, { status: "canceled" });
      } else {
        patch(id, {
          status: "error",
          error: err?.response?.data?.detail || err?.message || "Erreur d'envoi",
        });
      }
    } finally {
      controllers.current.delete(id);
    }
  }, []);

  const enqueue = useCallback((fileOrFiles, meta) => {
    const files = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    const newEntries = files.map((f) => ({
      id: nextId(),
      file: f,
      status: "pending",
      progress: 0,
      uploadedBytes: 0,
      error: null,
      meta: meta || null,
    }));
    setEntries((prev) => [...prev, ...newEntries]);
    if (autoStart) {
      for (const e of newEntries) startUpload(e.id, e.file, undefined, e.meta);
    }
    return newEntries.map((e) => e.id);
  }, [autoStart, startUpload]);

  const cancel = useCallback((id) => {
    const c = controllers.current.get(id);
    if (c) c.abort();
  }, []);

  const remove = useCallback((id) => {
    // If still uploading, abort first
    const c = controllers.current.get(id);
    if (c) c.abort();
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const uploadAll = useCallback(async (customUploader) => {
    // Start every pending entry with the given uploader. Serial by default —
    // simpler UX (progress bars fill one after another) and avoids saturating
    // the tab's upload slots.
    const fn = customUploader || uploaderRef.current;
    if (!fn) return;
    const pending = entries.filter((e) => e.status === "pending");
    for (const e of pending) {
      await startUpload(e.id, e.file, fn, e.meta);
    }
  }, [entries, startUpload]);

  const reset = useCallback(() => {
    for (const c of controllers.current.values()) c.abort();
    controllers.current.clear();
    // Idempotent: don't re-create an empty array if we're already empty —
    // otherwise consumers that call reset() inside an effect that depends on
    // the queue would loop (new [] triggers re-render → new effect run …).
    setEntries((prev) => (prev.length === 0 ? prev : []));
  }, []);

  // Memoise the return object so its *identity* only changes when `entries`
  // does — consumers safely including the whole queue as a useEffect dep
  // won't fire on every render. All function fields are already useCallback-
  // stable, so we only need `entries` as the dep here.
  return useMemo(
    () => ({ entries, enqueue, cancel, remove, uploadAll, reset }),
    [entries, enqueue, cancel, remove, uploadAll, reset],
  );
}
